import { flowBuilderConstants } from '../../../constants/FlowBuilder';
import { CanvasElement, Orientation, StepElement } from '../canvas-element';
import { ConditionClickHandler, ConnectorBtnClickHandler, ConnectorDrawHandler, ConnectorSelectHandler, EgressClickHandler, ElementSelectHandler, FlowBuilderState, IntermediateConnector, ItemBtnClickHandler, StepCardMenuClickHandler } from '../TypeDefs';

/**
 * @method isClick
 * @description Check if its a click event -> (mouseUp - mouseDown) <= 500
 * @param mouseDownStart
 * @returns if its a click event
 */
export const isClick = (mouseDownStart: number, mousemove: { x: number, y: number }) =>
  Date.now() - mouseDownStart <= flowBuilderConstants.clickMaxDuration
  && mousemove.x <= flowBuilderConstants.clickMaxMousemove
  && mousemove.y <= flowBuilderConstants.clickMaxMousemove;

/**
 * @method canvasZoomOut
 * @description Perform standard zoom out on canvas
 * @param zoomLevel Current zoom level
 * @param translateX Current canvas translateX
 * @param translateY Current canvas translateY
 * @param width Canvas width
 * @param height Canvas height
 * @returns New value of zoom and translate
 */
export const canvasZoomOut = (
  zoomLevel: number,
  translateX: number,
  translateY: number,
  width: number,
  height: number,
) => {
  const prevZoom = zoomLevel;
  zoomLevel -= flowBuilderConstants.zoomDiffValue;
  if (zoomLevel < flowBuilderConstants.minZoomValue) {
    zoomLevel = flowBuilderConstants.minZoomValue;
  }
  /*  else if (zoomLevel > flowBuilderConstants.maxZoomValue) {
    zoomLevel = flowBuilderConstants.maxZoomValue;
  } */
  const mouseX = width / 2;
  const mouseY = height / 2;
  const zoomDelta = zoomLevel / prevZoom;
  translateX = (translateX - mouseX) * zoomDelta + mouseX;
  translateY = (translateY - mouseY) * zoomDelta + mouseY;
  return { zoomLevel, translateX, translateY };
};

/**
 * @method canvasZoomIn
 * @description Perform standard zoom in on canvas
 * @param zoomLevel Current zoom level
 * @param translateX Current canvas translateX
 * @param translateY Current canvas translateY
 * @param width Canvas width
 * @param height Canvas height
 * @returns New value of zoom and translate
 */
export const canvasZoomIn = (
  zoomLevel: number,
  translateX: number,
  translateY: number,
  width: number,
  height: number,
) => {
  const prevZoom = zoomLevel;
  zoomLevel += flowBuilderConstants.zoomDiffValue;
  /* if (zoomLevel < flowBuilderConstants.minZoomValue) {
    zoomLevel = flowBuilderConstants.minZoomValue;
  } else  */
  if (zoomLevel > flowBuilderConstants.maxZoomValue) {
    zoomLevel = flowBuilderConstants.maxZoomValue;
  }
  const mouseX = width / 2;
  const mouseY = height / 2;
  const zoomDelta = zoomLevel / prevZoom;
  translateX = (translateX - mouseX) * zoomDelta + mouseX;
  translateY = (translateY - mouseY) * zoomDelta + mouseY;
  return { zoomLevel, translateX, translateY };
};

/**
 * @method handleCanvasZoom
 * @description Handle canvas zoom based on the zoom level and mouse wheel delta.
 * @param {object} params - The parameters for handling canvas zoom.
 * @param {number} params.zoomLevel - The current zoom level.
 * @param {number} params.translateX - The current translation on the x-axis.
 * @param {number} params.translateY - The current translation on the y-axis.
 * @param {number} params.top - The top position of the canvas.
 * @param {number} params.left - The left position of the canvas.
 * @param {number} params.clientX - The x-coordinate of the mouse pointer.
 * @param {number} params.clientY - The y-coordinate of the mouse pointer.
 * @param {number} params.deltaY - The delta value of the mouse wheel.
 * @returns {object} The updated zoom level, translation on the x-axis, and translation on the y-axis.
 */
export const handleCanvasZoom = (
  {
    zoomLevel,
    translateX,
    translateY,
    top,
    left,
    clientX,
    clientY,
    deltaY
  }: {
    zoomLevel: number;
    translateX: number;
    translateY: number;
    top: number;
    left: number;
    clientX: number;
    clientY: number;
    deltaY: number;
  }
) => {
  const prevZoom = zoomLevel;
  zoomLevel -= deltaY / flowBuilderConstants.zoomWheelDelta;
  if (zoomLevel < flowBuilderConstants.minZoomValue) {
    zoomLevel = flowBuilderConstants.minZoomValue;
  } else if (zoomLevel > flowBuilderConstants.maxZoomValue) {
    zoomLevel = flowBuilderConstants.maxZoomValue;
  }
  const mouseX = clientX - left;
  const mouseY = clientY - top;
  const zoomDelta = zoomLevel / prevZoom;
  translateX = (translateX - mouseX) * zoomDelta + mouseX;
  translateY = (translateY - mouseY) * zoomDelta + mouseY;
  return { zoomLevel, translateX, translateY };
};

/**
 * @method slideElement
 * @description Slides an element on a canvas to a specified position.
 * @param {Object} options - The options for sliding the element.
 * @param {CanvasElement[]} options.canvasElements - The array of canvas elements.
 * @param {string} options.id - The ID of the element to slide.
 * @param {Object} options.to - The target position to slide the element to.
 * @param {number} options.to.x - The x-coordinate of the target position.
 * @param {number} options.to.y - The y-coordinate of the target position.
 * @param {number} options.duration - The duration of the slide animation.
 * @param {Function} options.drawElements - The function to redraw the canvas elements.
 * @returns {Promise<void>} A promise that resolves when the slide animation is complete.
 */
export const slideElement = ({
  canvasElements,
  id,
  to,
  duration,
  drawElements
}: {
  canvasElements: CanvasElement[],
  id: string,
  to: { x: number, y: number },
  duration: number,
  drawElements: () => void
}): Promise<void> => new Promise((resolve) => {
  const elem = canvasElements.find((e) => e.elementId === id);
  if (!elem) {
    resolve();
    return;
  }
  const animStart = Date.now();
  const from = {
    x: elem.left,
    y: elem.top
  };
  const diff = {
    x: to.x - from.x,
    y: to.y - from.y
  };
  const animate = () => {
    const timeDiff = (Date.now() - animStart) / duration / 2;
    const timeDelta = Math.sin(Math.PI * timeDiff);
    const movement = {
      x: diff.x * timeDelta,
      y: diff.y * timeDelta
    };
    elem.dragTo(from.x + movement.x, from.y + movement.y);
    drawElements();
    if (timeDiff < 0.5) {
      window.requestAnimationFrame(animate);
      return;
    }
    elem.dragTo(to.x, to.y);
    drawElements();
    resolve();
  };
  elem.dragStart(from.x, from.y);
  window.requestAnimationFrame(animate);
});

/**
 * @method handleCanvasElementClick
 * @description Handles the click event on a canvas element.
 * @param {Object} options - The options for handling the click event.
 * @param {number} options.mouseX - The x-coordinate of the mouse position.
 * @param {number} options.mouseY - The y-coordinate of the mouse position.
 * @param {number} options.clientX - The x-coordinate of the mouse position relative to the client area.
 * @param {number} options.clientY - The y-coordinate of the mouse position relative to the client area.
 * @param {CanvasElement} options.element - The canvas element that was clicked.
 * @param {Function} [options.onConnectorSelect] - The callback function for connector selection.
 * @param {Function} [options.onItemBtnClick] - The callback function for item button click.
 * @param {Function} [options.onConnectorBtnClick] - The callback function for connector button click.
 * @param {Function} [options.onConditionClick] - The callback function for condition click.
 * @param {Function} [options.onStepCardMenuClick] - The callback function for step card menu click.
 * @returns {boolean} Indicates whether the click event should propagate.
 */
const handleCanvasElementClick = ({
  mouseX,
  mouseY,
  clientX,
  clientY,
  element,
  onConnectorSelect,
  onItemBtnClick,
  onConnectorBtnClick,
  onConditionClick,
  onStepCardMenuClick,
}: {
  mouseX: number,
  mouseY: number,
  clientX: number,
  clientY: number,
  element: CanvasElement,
  onConnectorSelect?: ConnectorSelectHandler,
  onItemBtnClick?: ItemBtnClickHandler,
  onConnectorBtnClick?: ConnectorBtnClickHandler,
  onConditionClick?: ConditionClickHandler,
  onStepCardMenuClick?: StepCardMenuClickHandler,
}) => {
  const connector = element.mouseOnWhichConnectorLine(mouseX, mouseY);
  element.deselect();
  if (connector) {
    connector.select();
    onConnectorSelect?.call(null, {
      from: connector.fromElement?.elementId ?? '',
      to: connector.toElement.elementId
    });
    return false;
  }
  const b = element.mouseOnWhichButton(mouseX, mouseY);
  if (b) {
    onItemBtnClick?.call(null, b.id, element.elementId);
    return false;
  }
  const cb = element.mouseOnWhichConnectorBtn(mouseX, mouseY);
  if (cb) {
    onConnectorBtnClick?.call(null, cb.btn.id, { to: cb.line.toElement.elementId, from: cb.line.fromElement?.elementId ?? '' });
    return false;
  }
  if (element.isMouseOnCondition(mouseX, mouseY)) {
    onConditionClick?.call(null, element.elementId, clientX, clientY);
    return false;
  }
  if (element instanceof StepElement && element.isMouseOnStepMenuIcon(mouseX, mouseY)) {
    onStepCardMenuClick?.call(null, element.elementId, clientX, clientY);
    return false;
  }
  return true;
};

/**
 * @method handleCanvasClick
 * @description Handles the click event on the canvas.
 * @param {Object} options - The options for handling the click event.
 * @param {FlowBuilderState} options.flowBuilderState - The state of the flow builder.
 * @param {number} options.mouseX - The x-coordinate of the mouse position.
 * @param {number} options.mouseY - The y-coordinate of the mouse position.
 * @param {number} options.clientX - The x-coordinate of the mouse position relative to the client area.
 * @param {number} options.clientY - The y-coordinate of the mouse position relative to the client area.
 * @param {HTMLCanvasElement} options.canvas - The canvas element.
 * @param {Function} [options.onElementSelect] - The callback function for element selection.
 * @param {Function} [options.onConnectorSelect] - The callback function for connector selection.
 * @param {Function} [options.onItemBtnClick] - The callback function for item button click.
 * @param {Function} [options.onConnectorBtnClick] - The callback function for connector button click.
 * @param {Function} [options.onConditionClick] - The callback function for condition click.
 * @param {Function} [options.onStepCardMenuClick] - The callback function for step card menu click.
 */
export const handleCanvasClick = ({
  flowBuilderState,
  mouseX,
  mouseY,
  clientX,
  clientY,
  canvas,
  onElementSelect,
  onConnectorSelect,
  onItemBtnClick,
  onConnectorBtnClick,
  onConditionClick,
  onStepCardMenuClick,
}: {
  flowBuilderState: FlowBuilderState,
  mouseX: number,
  mouseY: number,
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement,
  onElementSelect?: ElementSelectHandler,
  onConnectorSelect?: ConnectorSelectHandler,
  onItemBtnClick?: ItemBtnClickHandler,
  onConnectorBtnClick?: ConnectorBtnClickHandler,
  onConditionClick?: ConditionClickHandler,
  onStepCardMenuClick?: StepCardMenuClickHandler,
}) => {
  if (flowBuilderState.panningCanvas) {
    flowBuilderState.cursorStyle = flowBuilderConstants.defaultCursor;
    canvas.style.cursor = flowBuilderState.cursorStyle;
  }
  let anyConnectorSelected = false;
  let anyBtnClicked = false;

  flowBuilderState.canvasElements.every((element) => handleCanvasElementClick({
    element,
    mouseX,
    mouseY,
    clientX,
    clientY,
    onConnectorSelect: (connector) => {
      onConnectorSelect?.call(null, connector);
      anyConnectorSelected = true;
    },
    onItemBtnClick: (btnId, nodeId) => {
      onItemBtnClick?.call(null, btnId, nodeId);
      anyBtnClicked = true;
    },
    onConnectorBtnClick,
    onConditionClick,
    onStepCardMenuClick,
  }));
  if (!anyBtnClicked) {
    // no element selected
    onElementSelect?.call(null);
  }

  if (!anyConnectorSelected) {
    // no connector selected
    onConnectorSelect?.call(null);
  }
};

/**
 * @method handleIntermediateConnectorDrawComplete
 * @description Handles the completion of drawing an intermediate connector.
 * @param {Object} options - The options for handling the completion of drawing an intermediate connector.
 * @param {number} options.mouseDownStart - The timestamp of when the mouse down event started.
 * @param {Object} options.mousemove - The movement of the mouse during the mouse down event.
 * @param {IntermediateConnector} options.drawingConnectorElement - The intermediate connector being drawn.
 * @param {CanvasElement[]} options.canvasElements - The array of canvas elements.
 * @param {number} options.mouseX - The x-coordinate of the mouse position.
 * @param {number} options.mouseY - The y-coordinate of the mouse position.
 * @param {Function} [options.onEgressClick] - The callback function for egress click.
 * @param {Function} [options.onConnectorDraw] - The callback function for connector draw.
 */
export const handleIntermediateConnectorDrawComplete = ({
  mouseDownStart,
  mousemove,
  drawingConnectorElement,
  canvasElements,
  mouseX,
  mouseY,
  onEgressClick,
  onConnectorDraw
}: {
  mouseDownStart: number,
  mousemove: { x: number, y: number },
  drawingConnectorElement: IntermediateConnector,
  canvasElements: CanvasElement[],
  mouseX: number,
  mouseY: number,
  onEgressClick?: EgressClickHandler,
  onConnectorDraw?: ConnectorDrawHandler,
}) => {
  if (!drawingConnectorElement.isIncoming && isClick(mouseDownStart, mousemove)) {
    // click on outgoing connector
    onEgressClick?.call(null, drawingConnectorElement.element.elementId, drawingConnectorElement.orientation, drawingConnectorElement.element instanceof CanvasElement ? 'element' : 'step');
    return;
  }
  let destElement: CanvasElement | undefined;
  let destOrientation: Orientation | null = null;

  // if drawing line, detect if mouse is on a different element's connector
  // only in -> out and out -> in allowed
  canvasElements.every((element) => {
    destOrientation = element.isMouseOnBody(mouseX, mouseY) ? Orientation.HORIZONTAL : null;
    if (
      element !== drawingConnectorElement.element && destOrientation
    ) {
      destElement = element;
      return false;
    }
    return true;
  });
  // if on a different elements's connector trigger event/draw the line
  if (destElement) {
    let fromElement = drawingConnectorElement.element;
    let toElement = destElement;
    const orientation = {
      from: drawingConnectorElement.orientation,
      to: destOrientation ?? drawingConnectorElement.orientation
    };
    if (drawingConnectorElement.isIncoming) {
      fromElement = destElement;
      toElement = drawingConnectorElement.element;
      orientation.from = orientation.to;
      orientation.to = drawingConnectorElement.orientation;
    }
    onConnectorDraw?.call(null, fromElement.elementId, toElement.elementId, orientation);
  }
};

/**
 * @method calculateCursorAndTooltipOnCanvasElement
 * @description Calculates the cursor and tooltip for a given mouse position on canvas elements.
 * @param {Object} options - The options for calculating the cursor and tooltip.
 * @param {CanvasElement[]} options.canvasElements - The array of canvas elements.
 * @param {number} options.mouseX - The x-coordinate of the mouse position.
 * @param {number} options.mouseY - The y-coordinate of the mouse position.
 * @returns {Object} An object containing the cursor and tooltip.
 * @property {string} cursor - The cursor style.
 * @property {string} [tooltip] - The tooltip text.
 */
export const calculateCursorAndTooltipOnCanvasElement = ({
  canvasElements,
  mouseX,
  mouseY,
}: {
  canvasElements: CanvasElement[],
  mouseX: number,
  mouseY: number,
}): { cursor: string, tooltip?: string } => {
  let tooltip: string | undefined;
  let cursor: string = flowBuilderConstants.defaultCursor;
  // change cursor for different elements
  // Move - when on item body
  // Pointer - when of connectors
  canvasElements.every((element) => {
    if (
      element.isMouseOnIncomingConnector(mouseX, mouseY)
      || element.isMouseOnOutgoingConnector(mouseX, mouseY)
      || element.isMouseOnCondition(mouseX, mouseY)
      || (element instanceof StepElement && element.isMouseOnStepMenuIcon(mouseX, mouseY))
    ) {
      cursor = 'pointer';
      return false;
    }
    const b = element.mouseOnWhichButton(mouseX, mouseY);
    if (b) {
      tooltip = b.label;
      cursor = 'pointer';
      return false;
    }
    const cb = element.mouseOnWhichConnectorBtn(mouseX, mouseY);
    if (cb) {
      tooltip = cb.btn.label;
      cursor = 'pointer';
      return false;
    }

    element.removeConnectorHoverEffect();
    let c = element.mouseOnWhichConnectorText(mouseX, mouseY);
    if (c) {
      tooltip = c.tooltipText;
      c.performHoverEffect();
    }
    c = element.mouseOnWhichConnectorLine(mouseX, mouseY)
      ?? element.mouseOnWhichConnectorBtnGroupBody(mouseX, mouseY);
    if (c) {
      cursor = 'pointer';
      c.performHoverEffect();
    }

    if (element.isMouseOnBody(mouseX, mouseY) || element.isMouseOnBtnGroupBody(mouseX, mouseY)) {
      if (!(element instanceof StepElement)) {
        tooltip = element.name;
      }
      cursor = 'move';
      element.performHoverEffect();
    } else {
      element.removeHoverEffect();
    }
    return true;
  });

  return { cursor, tooltip };
};
